import {
  buildLayerPanelStateBundle,
  toggleExpandedLayerIds,
} from "./map-layer-panel-state.js";
import { nextHoverFactVisibilityByLayer } from "./map-hover-facts.js";
import { parseLayerHighlightColor } from "./map-layer-highlight-style.js";
import { renderLayerStack } from "./map-layer-panel.js";
import {
  dispatchMapLayerFilterChange,
  dispatchMapLayerPresentationChange,
} from "./map-control-events.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import { FISHYMAP_ZONE_CATALOG_READY_EVENT } from "./map-zone-catalog-live.js";
import {
  buildLayerClipMaskPatch,
  buildLayerConfidenceShowcasePatch,
  buildLayerHighlightsPatch,
  buildLayerOpacityPatch,
  buildLayerPointIconsPatch,
  buildLayerPointIconScalePatch,
  buildLayerPointClusteringPatch,
  buildLayerWaypointConnectionsPatch,
  buildLayerWaypointLabelsPatch,
  clampLayerOpacity,
  clampPointIconScale,
  layerOpacityLabel,
  layerOpacityValue,
  moveLayerIdBefore,
  pointIconScaleLabel,
  pointIconScaleValue,
  resolveLayerEntries,
  resolveVisibleLayerIds,
} from "./map-layer-state.js";

const LAYER_PANEL_TAG_NAME = "fishymap-layer-panel";
const FISHYMAP_V2_EVENT = "fishymap:v2";
const DRAG_ATTACH_EDGE_PX_MIN = 14;
const DRAG_ATTACH_EDGE_PX_MAX = 22;
const HTMLElementBase = globalThis.HTMLElement ?? class {};

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      (
        {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[char] || char
      ),
  );
}

function spriteIcon(name, sizeClass = "size-5") {
  return `<svg class="fishy-icon ${sizeClass}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${name}"></use></svg>`;
}

function eyeIcon(visible) {
  return visible ? spriteIcon("eye") : spriteIcon("eye-slash");
}

function renderLoadingInlineMarkup(label, { size = "xs", toneClass = "text-base-content/70" } = {}) {
  return `<span class="inline-flex items-center gap-2 ${toneClass}"><span class="loading loading-spinner loading-${escapeHtml(size)}" aria-hidden="true"></span><span>${escapeHtml(label)}</span></span>`;
}

function renderLoadingPanelMarkup(label) {
  return `<div class="rounded-box border border-base-300/70 bg-base-200 px-3 py-3"><div class="flex items-center gap-2 text-xs text-base-content/60">${renderLoadingInlineMarkup(label)}</div></div>`;
}

function setTextContent(element, text) {
  if (!element) {
    return;
  }
  element.textContent = String(text ?? "");
}

function normalizeExpandedLayerIds(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next;
}

function syncLayerOpacityControl(container, layerId, opacity) {
  if (!container || !layerId) {
    return false;
  }
  const slider = Array.from(container.querySelectorAll("input[data-layer-opacity]")).find(
    (candidate) => candidate.getAttribute("data-layer-opacity") === layerId,
  );
  if (!slider) {
    return false;
  }
  const normalized = clampLayerOpacity(opacity);
  const value = layerOpacityValue(normalized);
  if (slider.value !== value) {
    slider.value = value;
  }
  const label = slider
    .closest(".fishymap-layer-opacity-control")
    ?.querySelector?.("[data-layer-opacity-value]");
  if (label) {
    label.textContent = layerOpacityLabel(normalized);
  }
  return true;
}

function syncLayerHighlightOpacityControl(container, layerId, opacity) {
  if (!container || !layerId) {
    return false;
  }
  const slider = Array.from(container.querySelectorAll("input[data-layer-highlight-opacity]")).find(
    (candidate) => candidate.getAttribute("data-layer-highlight-opacity") === layerId,
  );
  if (!slider) {
    return false;
  }
  const normalized = clampLayerOpacity(opacity);
  const value = layerOpacityValue(normalized);
  if (slider.value !== value) {
    slider.value = value;
  }
  const label = slider
    .closest(".fishymap-layer-highlight-opacity-control")
    ?.querySelector?.("[data-layer-highlight-opacity-value]");
  if (label) {
    label.textContent = layerOpacityLabel(normalized);
  }
  return true;
}

function syncLayerPointIconScaleControl(container, layerId, scale) {
  if (!container || !layerId) {
    return false;
  }
  const slider = Array.from(container.querySelectorAll("input[data-layer-point-icon-scale]")).find(
    (candidate) => candidate.getAttribute("data-layer-point-icon-scale") === layerId,
  );
  if (!slider) {
    return false;
  }
  const normalized = clampPointIconScale(scale);
  const value = pointIconScaleValue(normalized);
  if (slider.value !== value) {
    slider.value = value;
  }
  const label = slider
    .closest(".fieldset")
    ?.querySelector?.("[data-layer-point-icon-scale-value]");
  if (label) {
    label.textContent = pointIconScaleLabel(normalized);
  }
  return true;
}

export class FishyMapLayerPanelElement extends HTMLElementBase {
  constructor() {
    super();
    this._shell = null;
    this._mapShellProjection = null;
    this._liveEventsEligible = false;
    this._connected = false;
    this._rafId = 0;
    this._zoneCatalog = [];
    this._state = {
      activeSettingsTabByLayer: new Map(),
      activeOpacityLayerId: "",
      activeOpacityValue: null,
      activeHighlightOpacityLayerId: "",
      activeHighlightOpacityValue: null,
      activePointIconScaleLayerId: "",
      activePointIconScaleValue: null,
      focusSettingsTabLayerId: "",
      draggingLayerId: "",
      overLayerId: "",
      dropMode: "",
      hover: null,
    };
    this._count = null;
    this._canvas = null;
    this._handleZoneCatalogReady = (event) => {
      this._zoneCatalog = Array.isArray(event?.detail?.zoneCatalog) ? cloneJson(event.detail.zoneCatalog) : [];
      this.scheduleRender();
    };
    this._handleHoverChanged = (event) => {
      this._state.hover =
        event?.detail?.hover && typeof event.detail.hover === "object"
          ? cloneJson(event.detail.hover)
          : null;
      if (this.hasExpandedLayerSettings()) {
        this.scheduleRender();
      }
    };
    this._handleV2EventBatch = (event) => {
      const events = Array.isArray(event?.detail?.events) ? event.detail.events : [];
      for (const entry of events) {
        if (entry?.type === "hover-changed") {
          this._handleHoverChanged({ detail: { hover: entry } });
        }
      }
    };
    this._handleCanvasPointerLeave = () => {
      if (!this._state.hover) {
        return;
      }
      this._state.hover = null;
      if (this.hasExpandedLayerSettings()) {
        this.scheduleRender();
      }
    };
    this._handleClick = (event) => {
      const visibilityButton = event.target.closest("button[data-layer-visibility]");
      if (visibilityButton) {
        if (visibilityButton.disabled === true) {
          return;
        }
        const layerId = String(visibilityButton.getAttribute("data-layer-visibility") || "").trim();
        if (!layerId) {
          return;
        }
        const bundle = this.bundle();
        const visibleIds = new Set(resolveVisibleLayerIds(bundle));
        if (visibleIds.has(layerId)) {
          visibleIds.delete(layerId);
        } else {
          visibleIds.add(layerId);
        }
        const orderedVisibleIds = resolveLayerEntries(bundle)
          .map((candidate) => candidate.layerId)
          .filter((candidateId) => visibleIds.has(candidateId));
        this.dispatchFilterChange({
          version: 1,
          kind: "visibility",
          visibleLayerIds: orderedVisibleIds,
        });
        return;
      }

      const settingsTab = event.target.closest("button[data-layer-settings-tab]");
      if (settingsTab) {
        const layerId = String(
          settingsTab.getAttribute("data-layer-settings-layer-id") || "",
        ).trim();
        const tab = String(settingsTab.getAttribute("data-layer-settings-tab") || "").trim();
        if (!layerId || !tab) {
          return;
        }
        this._state.activeSettingsTabByLayer.set(layerId, tab);
        this._state.focusSettingsTabLayerId = layerId;
        this.scheduleRender();
        return;
      }

      const settingsTarget = event.target.closest("[data-layer-settings-toggle]");
      if (!settingsTarget) {
        return;
      }
      const layerId = String(settingsTarget.getAttribute("data-layer-settings-toggle") || "").trim();
      if (!layerId) {
        return;
      }
      this.writeExpandedLayerIds(
        toggleExpandedLayerIds(
          this._mapShellProjection?.ui?.layers?.expandedLayerIds,
          layerId,
        ),
      );
    };
    this._handleKeyDown = (event) => {
      const settingsTab = event.target.closest("button[data-layer-settings-tab]");
      if (
        settingsTab &&
        ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)
      ) {
        const tabs = Array.from(
          settingsTab
            .closest("[role='tablist']")
            ?.querySelectorAll?.("button[data-layer-settings-tab]") || [],
        );
        const currentIndex = tabs.indexOf(settingsTab);
        if (currentIndex < 0 || !tabs.length) {
          return;
        }
        let nextIndex = currentIndex;
        if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = tabs.length - 1;
        } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextIndex = (currentIndex + 1) % tabs.length;
        } else {
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        }
        event.preventDefault();
        tabs[nextIndex].click?.();
        return;
      }
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      if (event.target.closest("button[data-layer-visibility]")) {
        return;
      }
      const settingsTarget = event.target.closest(".fishymap-layer-summary[data-layer-settings-toggle]");
      if (!settingsTarget) {
        return;
      }
      event.preventDefault();
      settingsTarget.click?.();
    };
    this._handleChange = (event) => {
      const tradeNpcClassToggle = event.target.closest("input[data-layer-trade-npc-class]");
      if (tradeNpcClassToggle) {
        const classesVisible = Array.from(
          this.querySelectorAll("input[data-layer-trade-npc-class]"),
        )
          .filter((toggle) => toggle.checked)
          .map((toggle) =>
            String(toggle.getAttribute("data-layer-trade-npc-class") || "").trim()
          )
          .filter(Boolean);
        this.dispatchFilterChange({
          version: 1,
          kind: "trade-npc-classes",
          classesVisible,
        });
        return;
      }

      const connectionToggle = event.target.closest("input[data-layer-waypoint-connections]");
      if (connectionToggle) {
        const layerId = String(connectionToggle.getAttribute("data-layer-waypoint-connections") || "").trim();
        if (!layerId) {
          return;
        }
        const next = buildLayerWaypointConnectionsPatch(this.bundle(), layerId, connectionToggle.checked);
        this.writeLayerFilterOverride("waypoint-connections", layerId, next);
        return;
      }

      const labelToggle = event.target.closest("input[data-layer-waypoint-labels]");
      if (labelToggle) {
        const layerId = String(labelToggle.getAttribute("data-layer-waypoint-labels") || "").trim();
        if (!layerId) {
          return;
        }
        const next = buildLayerWaypointLabelsPatch(this.bundle(), layerId, labelToggle.checked);
        this.writeLayerFilterOverride("waypoint-labels", layerId, next);
        return;
      }

      const pointIconsToggle = event.target.closest("input[data-layer-point-icons]");
      if (pointIconsToggle) {
        const layerId = String(pointIconsToggle.getAttribute("data-layer-point-icons") || "").trim();
        if (!layerId) {
          return;
        }
        const next = buildLayerPointIconsPatch(this.bundle(), layerId, pointIconsToggle.checked);
        this.writeLayerFilterOverride("point-icons", layerId, next);
        return;
      }

      const pointClusteringToggle = event.target.closest(
        "input[data-layer-point-clustering]",
      );
      if (pointClusteringToggle) {
        const layerId = String(
          pointClusteringToggle.getAttribute("data-layer-point-clustering") || "",
        ).trim();
        if (!layerId) {
          return;
        }
        const next = buildLayerPointClusteringPatch(
          this.bundle(),
          layerId,
          pointClusteringToggle.checked,
        );
        this.writeLayerFilterOverride("point-clustering", layerId, next);
        return;
      }

      const confidenceShowcaseToggle = event.target.closest(
        "input[data-layer-confidence-showcase]",
      );
      if (confidenceShowcaseToggle) {
        const layerId = String(
          confidenceShowcaseToggle.getAttribute("data-layer-confidence-showcase") || "",
        ).trim();
        if (!layerId) {
          return;
        }
        const next = buildLayerConfidenceShowcasePatch(
          this.bundle(),
          layerId,
          confidenceShowcaseToggle.checked,
        );
        this.writeLayerFilterOverride("confidence-showcase", layerId, next);
        return;
      }

      const lineagePanelToggle = event.target.closest("input[data-layer-lineage-panel]");
      if (lineagePanelToggle) {
        const layerId = String(
          lineagePanelToggle.getAttribute("data-layer-lineage-panel") || "",
        ).trim();
        if (!layerId) {
          return;
        }
        this.dispatchPresentationChange({
          version: 1,
          kind: "lineage",
          layerId,
          visible: lineagePanelToggle.checked,
        });
        this.scheduleRender();
        return;
      }

      const highlightToggle = event.target.closest("input[data-layer-highlight-enabled]");
      if (highlightToggle) {
        const layerId = String(
          highlightToggle.getAttribute("data-layer-highlight-enabled") || "",
        ).trim();
        if (!layerId) {
          return;
        }
        const next = buildLayerHighlightsPatch(this.bundle(), layerId, {
          enabled: highlightToggle.checked,
        });
        this.writeLayerFilterOverride("highlight", layerId, next);
        return;
      }

      const highlightColor = event.target.closest("input[data-layer-highlight-color]");
      if (highlightColor) {
        const layerId = String(
          highlightColor.getAttribute("data-layer-highlight-color") || "",
        ).trim();
        const color = parseLayerHighlightColor(highlightColor.value);
        if (!layerId || !color) {
          return;
        }
        const next = buildLayerHighlightsPatch(this.bundle(), layerId, { color });
        this.writeLayerFilterOverride("highlight", layerId, next);
        return;
      }

      const sampleHoverToggle = event.target.closest("input[data-layer-sample-hover]");
      if (sampleHoverToggle) {
        const layerId = String(sampleHoverToggle.getAttribute("data-layer-sample-hover") || "").trim();
        if (!layerId) {
          return;
        }
        this.dispatchPresentationChange({
          version: 1,
          kind: "sample-hover",
          layerId,
          visible: sampleHoverToggle.checked,
        });
        this.scheduleRender();
        return;
      }

      const hoverFactToggle = event.target.closest("input[data-layer-hover-fact-key]");
      if (hoverFactToggle) {
        const layerId = String(
          hoverFactToggle.getAttribute("data-layer-hover-fact-layer-id") || "",
        ).trim();
        const factKey = String(hoverFactToggle.getAttribute("data-layer-hover-fact-key") || "").trim();
        if (!layerId || !factKey) {
          return;
        }
        const nextVisibility = nextHoverFactVisibilityByLayer(
          this._mapShellProjection?.ui?.layers?.hoverFactsVisibleByLayer,
          layerId,
          factKey,
          hoverFactToggle.checked,
        );
        this.dispatchPresentationChange({
          version: 1,
          kind: "hover-facts",
          layerId,
          visibleFacts: nextVisibility[layerId] || {},
        });
        this.scheduleRender();
        return;
      }

      const pointIconScaleSlider = event.target.closest("input[data-layer-point-icon-scale]");
      if (pointIconScaleSlider) {
        this._state.activePointIconScaleLayerId = "";
        this._state.activePointIconScaleValue = null;
        this.scheduleRender();
        return;
      }

      const highlightOpacitySlider = event.target.closest(
        "input[data-layer-highlight-opacity]",
      );
      if (highlightOpacitySlider) {
        this._state.activeHighlightOpacityLayerId = "";
        this._state.activeHighlightOpacityValue = null;
        this.scheduleRender();
        return;
      }

      const opacitySlider = event.target.closest("input[data-layer-opacity]");
      if (opacitySlider) {
        this._state.activeOpacityLayerId = "";
        this._state.activeOpacityValue = null;
        this.scheduleRender();
      }
    };
    this._handleInput = (event) => {
      const pointIconScaleSlider = event.target.closest("input[data-layer-point-icon-scale]");
      if (pointIconScaleSlider) {
        const layerId = String(pointIconScaleSlider.getAttribute("data-layer-point-icon-scale") || "").trim();
        if (!layerId) {
          return;
        }
        this._state.activePointIconScaleLayerId = layerId;
        this._state.activePointIconScaleValue = clampPointIconScale(pointIconScaleSlider.value);
        syncLayerPointIconScaleControl(this, layerId, this._state.activePointIconScaleValue);
        const next = buildLayerPointIconScalePatch(this.bundle(), layerId, pointIconScaleSlider.value);
        this.writeLayerFilterOverride("point-icon-scale", layerId, next);
        return;
      }

      const highlightOpacitySlider = event.target.closest(
        "input[data-layer-highlight-opacity]",
      );
      if (highlightOpacitySlider) {
        const layerId = String(
          highlightOpacitySlider.getAttribute("data-layer-highlight-opacity") || "",
        ).trim();
        if (!layerId) {
          return;
        }
        this._state.activeHighlightOpacityLayerId = layerId;
        this._state.activeHighlightOpacityValue = clampLayerOpacity(
          highlightOpacitySlider.value,
        );
        syncLayerHighlightOpacityControl(
          this,
          layerId,
          this._state.activeHighlightOpacityValue,
        );
        const next = buildLayerHighlightsPatch(this.bundle(), layerId, {
          opacity: this._state.activeHighlightOpacityValue,
        });
        this.writeLayerFilterOverride("highlight", layerId, next);
        return;
      }

      const opacitySlider = event.target.closest("input[data-layer-opacity]");
      if (!opacitySlider) {
        return;
      }
      const layerId = String(opacitySlider.getAttribute("data-layer-opacity") || "").trim();
      if (!layerId) {
        return;
      }
      this._state.activeOpacityLayerId = layerId;
      this._state.activeOpacityValue = clampLayerOpacity(opacitySlider.value);
      syncLayerOpacityControl(this, layerId, this._state.activeOpacityValue);
      const next = buildLayerOpacityPatch(this.bundle(), layerId, opacitySlider.value);
      this.writeLayerFilterOverride("opacity", layerId, next);
    };
    this._handleFocusOut = (event) => {
      const pointIconScaleSlider = event.target.closest("input[data-layer-point-icon-scale]");
      if (pointIconScaleSlider) {
        queueMicrotask(() => {
          this._state.activePointIconScaleLayerId = "";
          this._state.activePointIconScaleValue = null;
          this.scheduleRender();
        });
        return;
      }
      const highlightOpacitySlider = event.target.closest(
        "input[data-layer-highlight-opacity]",
      );
      if (highlightOpacitySlider) {
        queueMicrotask(() => {
          this._state.activeHighlightOpacityLayerId = "";
          this._state.activeHighlightOpacityValue = null;
          this.scheduleRender();
        });
        return;
      }
      const opacitySlider = event.target.closest("input[data-layer-opacity]");
      if (!opacitySlider) {
        return;
      }
      queueMicrotask(() => {
        this._state.activeOpacityLayerId = "";
        this._state.activeOpacityValue = null;
        this.scheduleRender();
      });
    };
    this._handleDragStart = (event) => {
      if (event.target.closest("button[data-layer-visibility]")) {
        event.preventDefault();
        return;
      }
      const handle = event.target.closest(".fishymap-layer-summary[data-layer-drag][draggable='true']");
      const card = handle?.closest(".fishymap-layer-card");
      if (!handle || !card) {
        return;
      }
      const layerId = String(card.getAttribute("data-layer-id") || "").trim();
      if (!layerId) {
        return;
      }
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", layerId);
        if (typeof event.dataTransfer.setDragImage === "function") {
          const rect = card.getBoundingClientRect();
          const offsetX = Number.isFinite(rect.width)
            ? Math.max(0, Math.min(rect.width, event.clientX - rect.left))
            : 0;
          const offsetY = Number.isFinite(rect.height)
            ? Math.max(0, Math.min(rect.height, event.clientY - rect.top))
            : 0;
          event.dataTransfer.setDragImage(card, offsetX, offsetY);
        }
      }
      this._state.draggingLayerId = layerId;
      card.dataset.dragging = "true";
    };
    this._handleDragOver = (event) => {
      if (!this._state.draggingLayerId) {
        return;
      }
      event.preventDefault();
      const card = event.target.closest(".fishymap-layer-card");
      if (!card) {
        this.clearDropState();
        return;
      }
      const targetLayerId = String(card.getAttribute("data-layer-id") || "").trim();
      if (!targetLayerId || targetLayerId === this._state.draggingLayerId) {
        this.clearDropState();
        return;
      }
      const rect = card.getBoundingClientRect();
      const offsetY = event.clientY - rect.top;
      const locked = card.getAttribute("data-locked") === "true";
      const canAttach = card.getAttribute("data-clip-mask-source") === "true";
      let mode = "before";
      if (locked) {
        mode = "before";
      } else if (!canAttach) {
        mode = offsetY >= rect.height / 2 ? "after" : "before";
      } else {
        const edgeThreshold = Math.max(
          DRAG_ATTACH_EDGE_PX_MIN,
          Math.min(rect.height * 0.24, DRAG_ATTACH_EDGE_PX_MAX),
        );
        if (offsetY <= edgeThreshold) {
          mode = "before";
        } else if (offsetY >= rect.height - edgeThreshold) {
          mode = "after";
        } else {
          mode = "attach";
        }
      }
      this.applyDropState(targetLayerId, mode);
    };
    this._handleDrop = (event) => {
      if (!this._state.draggingLayerId || !this._state.overLayerId || !this._state.dropMode) {
        this.clearDropState();
        return;
      }
      event.preventDefault();
      const bundle = this.bundle();
      const nextOrder = moveLayerIdBefore(
        resolveLayerEntries(bundle),
        this._state.draggingLayerId,
        this._state.overLayerId,
        this._state.dropMode === "after" ? "after" : "before",
      );
      const nextClipMasks = buildLayerClipMaskPatch(
        bundle,
        this._state.draggingLayerId,
        this._state.dropMode === "attach" ? this._state.overLayerId : "",
      );
      this.dispatchFilterChange({
        version: 1,
        kind: "stack",
        orderedLayerIds: nextOrder,
        clipMaskByLayer: nextClipMasks,
      });
      this._state.draggingLayerId = "";
      this.clearDropState();
    };
    this._handleDragEnd = () => {
      this._state.draggingLayerId = "";
      this.querySelectorAll(".fishymap-layer-card[data-dragging]").forEach((card) => {
        delete card.dataset.dragging;
      });
      this.clearDropState();
    };
    this._handleDragLeave = (event) => {
      const related = event.relatedTarget;
      if (related instanceof Node && this.contains(related)) {
        return;
      }
      this.clearDropState();
    };
  }

  connectedCallback() {
    this._connected = true;
    this._shell = this.closest?.("#map-page-shell") || null;
    this._count = this._shell?.querySelector?.("#fishymap-layers-count") || null;
    this._canvas = this._shell?.querySelector?.("#bevy") || null;
    this.addEventListener("click", this._handleClick);
    this.addEventListener("keydown", this._handleKeyDown);
    this.addEventListener("change", this._handleChange);
    this.addEventListener("input", this._handleInput);
    this.addEventListener("focusout", this._handleFocusOut);
    this.addEventListener("dragstart", this._handleDragStart);
    this.addEventListener("dragover", this._handleDragOver);
    this.addEventListener("drop", this._handleDrop);
    this.addEventListener("dragend", this._handleDragEnd);
    this.addEventListener("dragleave", this._handleDragLeave);
    this._canvas?.addEventListener?.("pointerleave", this._handleCanvasPointerLeave);
    this._shell?.addEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.addEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this.scheduleRender();
  }

  disconnectedCallback() {
    this._connected = false;
    this.removeEventListener("click", this._handleClick);
    this.removeEventListener("keydown", this._handleKeyDown);
    this.removeEventListener("change", this._handleChange);
    this.removeEventListener("input", this._handleInput);
    this.removeEventListener("focusout", this._handleFocusOut);
    this.removeEventListener("dragstart", this._handleDragStart);
    this.removeEventListener("dragover", this._handleDragOver);
    this.removeEventListener("drop", this._handleDrop);
    this.removeEventListener("dragend", this._handleDragEnd);
    this.removeEventListener("dragleave", this._handleDragLeave);
    this._canvas?.removeEventListener?.("pointerleave", this._handleCanvasPointerLeave);
    this._shell?.removeEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.removeEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    if (this._rafId && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(this._rafId);
    }
    this._rafId = 0;
    this._shell = null;
    this._count = null;
    this._canvas = null;
  }

  get mapShellProjection() {
    return this._mapShellProjection;
  }

  set mapShellProjection(value) {
    const projection = assertMapShellProjectionV1(value);
    const previousProjection = this._mapShellProjection;
    if (previousProjection) {
      if (projection.revision < previousProjection.revision) {
        throw new RangeError("Map shell projection revision cannot regress");
      }
      if (projection.revision === previousProjection.revision) {
        return;
      }
    }
    this._mapShellProjection = projection;
    if (
      this._connected
      && (
        !previousProjection
        || projection.ui !== previousProjection.ui
        || projection.bridged !== previousProjection.bridged
        || projection.runtime !== previousProjection.runtime
      )
    ) {
      this.scheduleRender();
    }
  }

  get liveEventsEligible() {
    return this._liveEventsEligible;
  }

  set liveEventsEligible(value) {
    if (typeof value !== "boolean") {
      throw new TypeError("Live Events eligibility must be a boolean");
    }
    if (this._liveEventsEligible === value) return;
    this._liveEventsEligible = value;
    if (this._connected) {
      this.scheduleRender();
    }
  }

  bundle() {
    return buildLayerPanelStateBundle({
      _map_runtime: this._mapShellProjection?.runtime || {},
      _map_bridged: this._mapShellProjection?.bridged || {},
    });
  }

  dispatchFilterChange(detail) {
    return dispatchMapLayerFilterChange(this, detail);
  }

  dispatchPresentationChange(detail) {
    return dispatchMapLayerPresentationChange(this, detail);
  }

  hasExpandedLayerSettings() {
    return normalizeExpandedLayerIds(
      this._mapShellProjection?.ui?.layers?.expandedLayerIds,
    ).length > 0;
  }

  writeExpandedLayerIds(nextExpandedLayerIds) {
    this.dispatchPresentationChange({
      version: 1,
      kind: "expanded-settings",
      expandedLayerIds: normalizeExpandedLayerIds(nextExpandedLayerIds),
    });
    this.scheduleRender();
  }

  writeLayerFilterOverride(kind, layerId, overrides) {
    this.dispatchFilterChange({
      version: 1,
      kind,
      layerId,
      override: Object.prototype.hasOwnProperty.call(overrides, layerId)
        ? overrides[layerId]
        : null,
    });
    this.scheduleRender();
  }

  clearDropState() {
    this._state.overLayerId = "";
    this._state.dropMode = "";
    this.querySelectorAll(".fishymap-layer-card[data-drop-position]").forEach((card) => {
      delete card.dataset.dropPosition;
    });
  }

  applyDropState(layerId, mode) {
    this._state.overLayerId = String(layerId || "");
    this._state.dropMode = String(mode || "");
    this.querySelectorAll(".fishymap-layer-card").forEach((card) => {
      if (card.getAttribute("data-layer-id") === this._state.overLayerId) {
        card.dataset.dropPosition = this._state.dropMode;
        return;
      }
      delete card.dataset.dropPosition;
    });
  }

  render() {
    this._rafId = 0;
    const projection = this._mapShellProjection;
    const bundle = this.bundle();
    const runtimeLayers = Array.isArray(projection?.runtime?.catalog?.layers)
      ? projection.runtime.catalog.layers
      : [];
    const expandedLayerIds = new Set(
      normalizeExpandedLayerIds(projection?.ui?.layers?.expandedLayerIds),
    );

    const activeOpacityInteraction =
      this._state.activeOpacityLayerId &&
      Number.isFinite(this._state.activeOpacityValue) &&
      syncLayerOpacityControl(this, this._state.activeOpacityLayerId, this._state.activeOpacityValue);
    const activeHighlightOpacityInteraction =
      !activeOpacityInteraction &&
      this._state.activeHighlightOpacityLayerId &&
      Number.isFinite(this._state.activeHighlightOpacityValue) &&
      syncLayerHighlightOpacityControl(
        this,
        this._state.activeHighlightOpacityLayerId,
        this._state.activeHighlightOpacityValue,
      );
    const activePointIconScaleInteraction =
      !activeOpacityInteraction &&
      !activeHighlightOpacityInteraction &&
      this._state.activePointIconScaleLayerId &&
      Number.isFinite(this._state.activePointIconScaleValue) &&
      syncLayerPointIconScaleControl(
        this,
        this._state.activePointIconScaleLayerId,
        this._state.activePointIconScaleValue,
      );

    if (
      !activeOpacityInteraction &&
      !activeHighlightOpacityInteraction &&
      !activePointIconScaleInteraction
    ) {
      renderLayerStack(
        this,
        bundle.state.ready === true ? bundle : { state: { catalog: { layers: [] } }, inputState: {} },
        {
          expandedLayerIds,
          activeSettingsTabByLayer: this._state.activeSettingsTabByLayer,
          hover: this._state.hover,
          selection: projection?.runtime?.selection || {},
          zoneCatalog: this._zoneCatalog,
          hoverFactVisibilityByLayer:
            projection?.ui?.layers?.hoverFactsVisibleByLayer || {},
          sampleHoverVisibleByLayer:
            projection?.ui?.layers?.sampleHoverVisibleByLayer || {},
          lineagePanelVisibleByLayer:
            projection?.ui?.layers?.lineagePanelVisibleByLayer || {},
          liveEventsEligible: this._liveEventsEligible,
          renderLoadingPanelMarkup,
          escapeHtml,
          eyeIcon,
        },
      );
      const focusLayerId = this._state.focusSettingsTabLayerId;
      const focusTab = this._state.activeSettingsTabByLayer.get(focusLayerId);
      if (focusLayerId && focusTab) {
        const tab = Array.from(this.querySelectorAll("button[data-layer-settings-tab]")).find(
          (candidate) =>
            candidate.getAttribute("data-layer-settings-layer-id") === focusLayerId &&
            candidate.getAttribute("data-layer-settings-tab") === focusTab,
        );
        if (tab) {
          this._state.focusSettingsTabLayerId = "";
          tab.focus?.();
        }
      }
    }

    setTextContent(this._count, bundle.state.ready === true ? runtimeLayers.length : 0);

    if (this._state.draggingLayerId) {
      const draggingCard = this.querySelector(
        `.fishymap-layer-card[data-layer-id="${CSS.escape(this._state.draggingLayerId)}"]`,
      );
      if (draggingCard) {
        draggingCard.dataset.dragging = "true";
      }
    }
    if (this._state.overLayerId && this._state.dropMode) {
      this.applyDropState(this._state.overLayerId, this._state.dropMode);
    }
  }

  scheduleRender() {
    if (this._rafId && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(this._rafId);
    }
    if (typeof globalThis.requestAnimationFrame === "function") {
      this._rafId = globalThis.requestAnimationFrame(() => {
        this.render();
      }) || 0;
      if (this._rafId) {
        return;
      }
    }
    this.render();
  }
}

export function registerFishyMapLayerPanelElement(registry = globalThis.customElements) {
  if (!registry || typeof registry.get !== "function" || typeof registry.define !== "function") {
    return false;
  }
  if (registry.get(LAYER_PANEL_TAG_NAME)) {
    return true;
  }
  registry.define(LAYER_PANEL_TAG_NAME, FishyMapLayerPanelElement);
  return true;
}

registerFishyMapLayerPanelElement();
